import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ProxyRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ProxyRequest';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {XmlParserUtil} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/xml/XmlParserUtil';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {CookieUtil, CookieValue} from '@mintdata-wsp/common/util/CookieUtil';
import {EncodingUtil} from '@mintdata-wsp/common/util/EncodingUtil';
import {TextUtil} from '@mintdata-wsp/common/util/TextUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetAPI, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABinaryData} from '@mintdata/mint-kit/api/values/ABinaryData';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import {enc, HmacSHA1} from 'crypto-js';

// https://msdn.microsoft.com/en-us/query-bi/m/web-contents

type FetchFn = (method:string, url:string, body:BodyInit, headers:any) => Promise<Response>
const RESPONSE_FORMATS = ['json', 'text', 'xml', 'binary'];

export class WebFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'web';
    }

    @Function({
        title: 'GET_EXT(urlAddress)',
        tip: 'Extended fetching the contents of a URL',
        alias: 'GET_EXT',
        example: 'GET_EXT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public getExt(@Param({
                      title: 'url',
                      tip: 'The URL to fetch',
                      type: Type.String
                  }) url:AString,
                  @Param({
                      title: 'httpHeaders',
                      tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                      type: Type.Frame,
                      optional: true
                  }) headers:AFrame,
                  @Param({
                      title: 'includeHttpDetails',
                      tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                      type: Type.Boolean,
                      optional: true
                  }) includeHttpDetails:ABoolean,
                  @Param({
                      title: 'responseFormat',
                      tip: 'Response format',
                      type: Type.String,
                      optional: true
                  }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyRequest('GET', url, null, headers, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'POST_EXT(urlAddress)',
        tip: 'Extended fetching the contents of a URL',
        alias: 'POST_EXT',
        example: 'POST_EXT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public postExt(@Param({
                       title: 'url',
                       tip: 'The URL to POST to',
                       type: Type.String
                   }) url:AString,
                   @Param({
                       title: 'body',
                       tip: 'The body to use in POST',
                       type: Type.String,
                       optional: true
                   }) body:AString,
                   @Param({
                       title: 'httpHeaders',
                       tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                       type: Type.Frame,
                       optional: true
                   }) headers:AFrame,
                   @Param({
                       title: 'includeHttpDetails',
                       tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                       type: Type.Boolean,
                       optional: true
                   }) includeHttpDetails:ABoolean,
                   @Param({
                       title: 'responseFormat',
                       tip: 'Response format',
                       type: Type.String,
                       optional: true
                   }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyRequest('POST', url, body.value, headers, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'PUT_EXT(urlAddress)',
        tip: 'Extended fetching the contents of a URL',
        alias: 'PUT_EXT',
        example: 'PUT_EXT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public putExt(@Param({
                      title: 'url',
                      tip: 'The URL to PUT to',
                      type: Type.String
                  }) url:AString,
                  @Param({
                      title: 'body',
                      tip: 'The body to use in PUT',
                      type: Type.String,
                      optional: true
                  }) body:AString,
                  @Param({
                      title: 'httpHeaders',
                      tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                      type: Type.Frame,
                      optional: true
                  }) headers:AFrame,
                  @Param({
                      title: 'includeHttpDetails',
                      tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                      type: Type.Boolean,
                      optional: true
                  }) includeHttpDetails:ABoolean,
                  @Param({
                      title: 'responseFormat',
                      tip: 'Response format',
                      type: Type.String,
                      optional: true
                  }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyRequest('PUT', url, body.value, headers, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'PATCH_EXT(urlAddress)',
        tip: 'Extended fetching the contents of a URL',
        alias: 'PATCH_EXT',
        example: 'PATCH_EXT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public patchExt(@Param({
                        title: 'url',
                        tip: 'The URL to PATCH to',
                        type: Type.String
                    }) url:AString,
                    @Param({
                        title: 'body',
                        tip: 'The body to use in PATCH',
                        type: Type.String,
                        optional: true
                    }) body:AString,
                    @Param({
                        title: 'httpHeaders',
                        tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                        type: Type.Frame,
                        optional: true
                    }) headers:AFrame,
                    @Param({
                        title: 'includeHttpDetails',
                        tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                        type: Type.Boolean,
                        optional: true
                    }) includeHttpDetails:ABoolean,
                    @Param({
                        title: 'responseFormat',
                        tip: 'Response format',
                        type: Type.String,
                        optional: true
                    }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyRequest('PATCH', url, body.value, headers, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'DELETE_EXT(urlAddress)',
        tip: 'Extended DELETE request',
        alias: 'DELETE_EXT',
        example: 'DELETE_EXT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with response contents',
                tip: 'Frame with response contents'
            }
    })
    @bind
    public deleteExt(@Param({
                         title: 'url',
                         tip: 'Request URL',
                         type: Type.String
                     }) url:AString,
                     @Param({
                         title: 'httpHeaders',
                         tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                         type: Type.Frame,
                         optional: true
                     }) headers:AFrame,
                     @Param({
                         title: 'includeHttpDetails',
                         tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                         type: Type.Boolean,
                         optional: true
                     }) includeHttpDetails:ABoolean,
                     @Param({
                         title: 'responseFormat',
                         tip: 'Response format',
                         type: Type.String,
                         optional: true
                     }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyRequest('DELETE', url, null, headers, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'getUrl(urlAddress)',
        tip: 'Fetches the contents of a URL',
        alias: 'GET',
        example: 'GET("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public get(@Param({
                   title: 'url',
                   tip: 'The URL to fetch',
                   type: Type.String
               }) aURL:AString,
               @Param({
                   title: 'httpHeaders',
                   tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                   type: Type.Frame,
                   optional: true
               }) userHeaders:AFrame,
               @Param({
                   title: 'includeHttpDetails',
                   tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                   type: Type.Boolean,
                   optional: true
               }) includeHttpDetails:ABoolean,
               @Param({
                   title: 'responseFormat',
                   tip: 'Response format',
                   type: Type.String,
                   optional: true
               }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._fetchRequest('GET', aURL, null, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'POST(urlAddress)',
        tip: 'Fetches the contents of a URL',
        alias: 'POST',
        example: 'POST("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public post(@Param({
                    title: 'url',
                    tip: 'The URL to fetch',
                    type: Type.String
                }) aURL:AString,
                @Param({
                    title: 'body',
                    tip: 'The body to use in POST',
                    type: Type.String,
                    optional: true
                }) body:AString,
                @Param({
                    title: 'httpHeaders',
                    tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                    type: Type.Frame,
                    optional: true
                }) userHeaders:AFrame,
                @Param({
                    title: 'includeHttpDetails',
                    tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                    type: Type.Boolean,
                    optional: true
                }) includeHttpDetails:ABoolean,
                @Param({
                    title: 'responseFormat',
                    tip: 'Response format',
                    type: Type.String,
                    optional: true
                }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._fetchRequest('POST', aURL, body.value, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'POST_FORM(urlAddress)',
        tip: 'Posts data as a form to an URL',
        alias: 'POST_FORM',
        example: 'POST_FORM("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with response information',
                tip: 'Frame with response information'
            }
    })
    @bind
    public async postForm(@Param({
                              title: 'url',
                              tip: 'The URL to fetch',
                              type: Type.String
                          }) url:AString,
                          @Param({
                              title: 'dataFrame',
                              tip: 'Frame with form data to post',
                              type: Type.Frame
                          }) dataFrame:IFrame,
                          @Param({
                              title: 'httpHeaders',
                              tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                              type: Type.Frame,
                              optional: true
                          }) userHeaders:AFrame,
                          @Param({
                              title: 'includeHttpDetails',
                              tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                              type: Type.Boolean,
                              optional: true
                          }) includeHttpDetails:ABoolean,
                          @Param({
                              title: 'responseFormat',
                              tip: 'Response format',
                              type: Type.String,
                              optional: true
                          }) responseFormat:AString):Promise<IValue | AError>
    {
        if( !dataFrame.fieldCount || dataFrame.fieldCount < 2 )
            return new AError('#INVALID_FORM_DATA_FRAME');

        const formFields = {};
        const formData = new FormData();
        for( let row of dataFrame.getDataUnsafe() )
        {
            const coerced = CoercionUtils.coerce(row[0], Type.String, null);
            if( Type.isError(coerced) )
                return new AError('#INVALID_FORM_DATA_FIELD_NAME');

            const fieldName = (coerced as AString).value;
            if( formFields[fieldName] )
                return new AError('#DUPLICATE_FORM_FIELD_NAME');

            let fieldValue = null;
            let fieldFileName = null;
            if( Type.isInstanceOf(row[1], Type.BinaryData) )
            {
                fieldValue = new Blob([(row[1] as ABinaryData).value]);

                const coercedFileName = CoercionUtils.coerce(row[2], Type.String, null);
                if( !Type.isError(coercedFileName) )
                    fieldFileName = (coercedFileName as AString).value;
            }
            else
            {
                const coercedValue = CoercionUtils.coerce(row[1], Type.String, null);
                if( Type.isError(coercedValue) )
                    return new AError('#INVALID_FORM_DATA_FIELD_VALUE');

                fieldValue = (coercedValue as AString).value;
            }

            formFields[fieldName] = true;

            if( fieldFileName )
                formData.append(fieldName, fieldValue, fieldFileName);
            else
                formData.append(fieldName, fieldValue);
        }

        return this._fetchRequest('POST', url, formData, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'PUT(urlAddress)',
        tip: 'Fetches the contents of a URL',
        alias: 'PUT',
        example: 'PUT("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public put(@Param({title: 'url', tip: 'The URL to fetch', type: Type.String}) aURL:AString,
               @Param({
                   title: 'body',
                   tip: 'The body to use in PUT',
                   type: Type.String,
                   optional: true
               }) body:AString,
               @Param({
                   title: 'httpHeaders',
                   tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                   type: Type.Frame,
                   optional: true
               }) userHeaders:AFrame,
               @Param({
                   title: 'includeHttpDetails',
                   tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                   type: Type.Boolean,
                   optional: true
               }) includeHttpDetails:ABoolean,
               @Param({
                   title: 'responseFormat',
                   tip: 'Response format',
                   type: Type.String,
                   optional: true
               }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._fetchRequest('PUT', aURL, body.value, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'PATCH(urlAddress)',
        tip: 'Fetches the contents of a URL',
        alias: 'PATCH',
        example: 'PATCH("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public patch(@Param({
                     title: 'url',
                     tip: 'The URL to fetch',
                     type: Type.String
                 }) aURL:AString,
                 @Param({
                     title: 'body',
                     tip: 'The body to use in PATCH',
                     type: Type.String,
                     optional: true
                 }) body:AString,
                 @Param({
                     title: 'httpHeaders',
                     tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                     type: Type.Frame,
                     optional: true
                 }) userHeaders:AFrame,
                 @Param({
                     title: 'includeHttpDetails',
                     tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                     type: Type.Boolean,
                     optional: true
                 }) includeHttpDetails:ABoolean,
                 @Param({
                     title: 'responseFormat',
                     tip: 'Response format',
                     type: Type.String,
                     optional: true
                 }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._fetchRequest('PATCH', aURL, body.value, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'DELETE(urlAddress)',
        tip: 'Execute HTTP DELETE request',
        alias: 'DELETE',
        example: 'DELETE("http://mintdata.com/")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with response contents',
                tip: 'Frame with response contents'
            }
    })
    @bind
    public deleteNormal(@Param({
                            title: 'url',
                            tip: 'Request URL',
                            type: Type.String
                        }) aURL:AString,
                        @Param({
                            title: 'httpHeaders',
                            tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                            type: Type.Frame,
                            optional: true
                        }) userHeaders:AFrame,
                        @Param({
                            title: 'includeHttpDetails',
                            tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                            type: Type.Boolean,
                            optional: true
                        }) includeHttpDetails:ABoolean,
                        @Param({
                            title: 'responseFormat',
                            tip: 'Response format',
                            type: Type.String,
                            optional: true
                        }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._fetchRequest('DELETE', aURL, null, userHeaders, includeHttpDetails, responseFormat);
    }

    @Function({
        title: 'env()',
        tip: 'Extracts value of the variable from the hash-part of the URL of the design',
        alias: 'ENV',
        example: 'ENV("myAnchor")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with value of the parameter',
                tip: 'Returns parameter value as a string'
            }
    })
    @bind
    public env(@Param({
                   title: 'sheetAPI',
                   tip: 'API',
                   type: SheetType.SheetAPI
               })
                   api:ISheetAPI,
               @Param({
                   title: 'anchor',
                   tip: 'Id of the anchor',
                   type: Type.String
               }) aId:AString):AString | AError
    {
        const anchor = api.getSheetContext().getLocationHash();
        const parts = anchor.split('&');
        const resultPart = parts.filter(part => part.includes(aId.value));
        if( resultPart != undefined && resultPart.length > 0 )
        {
            return new AString(resultPart[0].split('=')[1]);
        }
        else
        {
            return new AError('No such value!');
        }
    }

    @Function({
        title: 'encodeURL()',
        tip: 'Encodes string to URL',
        alias: 'ENCODE_URL',
        example: 'ENCODE_URL("Some value")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with value of the parameter',
                tip: 'Returns properly url-encoded string'
            }
    })
    @bind
    public encodeURL(@Param({title: 'Value', tip: 'Value to encode', type: Type.String}) aUrl:AString):AString | AError
    {
        const stringToEncode = aUrl.value;
        return new AString(encodeURI(stringToEncode));

    }

    @Function({
        title: 'decodeURL()',
        tip: 'Decodes string from URL',
        alias: 'DECODE_URL',
        example: 'DECODE_URL("Some%20value")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with the URL',
                tip: 'Returns properly url-decoded string'
            }
    })
    @bind
    public decodeURL(@Param({title: 'Value', tip: 'Value to decode', type: Type.String}) aUrl:AString):AString | AError
    {
        const stringToEncode = aUrl.value;
        return new AString(decodeURI(stringToEncode));

    }

    @Function({
        title: 'PATH(part1, [, part2...])',
        tip: 'Joins values into a slas-separated string. If any parameter is a frame, that it\'s cells will be joined as well',
        alias: 'PATH',
        example: 'PATH("http://root.com", {"/api//" ; "/v0\\"}, "action")  => http://root.com/api/v0/action',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the concatenated path',
                type: Type.String
            }
    })
    @bind
    public path(@Param({
        title: 'parts',
        tip: 'Parts to concatenate',
        type: SheetType.AnyIValue,
        repeatable: true
    }) parts:IValue[]):AString | AError
    {
        const hrefOrError = this._constructHrefFromParts(parts);
        if( Type.isInstanceOf(hrefOrError, Type.Error) )
            return hrefOrError as AError;

        return new AString(hrefOrError as string);
    }

    private _constructHrefFromParts(parts:IValue[]):string | AError
    {
        const partsOrError = TextUtil.collectStringsFromIValues(parts);
        if( Type.isInstanceOf(partsOrError, Type.Error) )
            return partsOrError as AError;

        return (partsOrError as Array<String>)
            .join('/')
            .replace(/[/\\]+/g, '/')
            .replace(/\/$/, '')
            .replace(/^(https?):\/?/, '$1://');
    }

    @Function({
        title: 'URL(url, [, queryParamsFrame])',
        tip: 'Constructs full url from an url, and queryParams. If 1st argument is a frame, ' +
            'it\'s cells will be slash-joined to construct resulting url. ' +
            'If queryParams frame present, treats it\'s first column as keys, ' +
            'second - as values and constructs the query part of an url',
        alias: 'URL',
        example: 'URL({"http://root.com"; "api///"; "v0"; "action"}, {"a", 1; "b", 2; ..}})  => http://root.com/api/v0/action?a=1&b=2',
        icon: '',
        returns:
            {
                title: 'true',
                type: Type.String,
                tip: 'Returns the constructed url'
            }
    })
    @bind
    public url(@Param({
                   title: 'part',
                   tip: 'Url or frame with url parts',
                   type: SheetType.AnyIValue
               }) url:IValue,
               @Param({
                   title: 'queryParams',
                   tip: 'Query params frame',
                   type: Type.Frame,
                   optional: true
               }) queryParams:IFrame):AString | AError
    {

        if( queryParams && queryParams.recordCount > 0 && queryParams.fieldCount < 2 )
            return new AError('#INVALID_QUERY_PARAMS');

        const hrefOrError = this._constructHrefFromParts([url]);
        if( Type.isInstanceOf(hrefOrError, Type.Error) )
            return hrefOrError as AError;

        let href = hrefOrError as string;

        if( queryParams )
        {
            const qpPairs = this._coerceQueryParamsFrameToString(queryParams);

            if( qpPairs.length > 0 )
            {
                // this regexp is from here:  https://stackoverflow.com/a/21553982/2260777
                const match = href.match(/([^?]*)(\?[^#]*)?(\#.*)?/);
                const url = match[1] || '';
                const query = match[2] ? match[2] + '&' : '?';
                const hash = match[3] || '';

                href = url + query + qpPairs.join('&') + hash;
            }
        }

        return new AString(href);
    }

    private _coerceQueryParamsFrameToString(queryParams:IFrame):string[]
    {
        const qpPairs = [];
        queryParams.getRecords()
            .forEach(record =>
            {
                const cells = record.getCells();

                const coercedKey = CoercionUtils.coerce(cells[0], Type.String, null);
                const coercedValue = CoercionUtils.coerce(cells[1], Type.String, null);
                if( [coercedKey, coercedValue].some(coerced => Type.isInstanceOf(coerced, Type.Error)) )
                    return new AError('#CANT_COERCE_QUERY_PARAM_TO_STRING');

                const sKey = encodeURIComponent((coercedKey as AString).value);
                const sValue = encodeURIComponent((coercedValue as AString).value);
                qpPairs.push(`${sKey}=${sValue}`);
            });

        return qpPairs;
    }

    @Function({
        title: 'GET_COOKIE(cookieName)',
        tip: 'Get cookie value',
        alias: 'GET_COOKIE',
        example: 'GET_COOKIE("session_id")',
        icon: '',
        returns:
            {
                title: 'result',
                type: Type.String,
                tip: 'Returns cookie value'
            }
    })
    @bind
    public getCookie(@Param({
        title: 'cookieName',
        tip: 'Cookie Name',
        type: Type.String
    }) cookieName:AString):AString | AEmpty
    {
        const cookieValue = CookieUtil.getCookie(cookieName.value);

        return cookieValue ? new AString(cookieValue) : AEmpty.Value;
    }

    @Function({
        title: 'SET_COOKIE(cookieName, cookieValue)',
        tip: 'Set cookie value',
        alias: 'SET_COOKIE',
        example: 'SET_COOKIE("session_id", "the-value")',
        icon: '',
        returns:
            {
                title: 'result',
                type: Type.String,
                tip: 'Returns cookie value'
            }
    })
    @bind
    public SetCookie(@Param({
                         title: 'cookieName',
                         tip: 'Cookie Name',
                         type: Type.String
                     }) cookieName:AString,
                     @Param({
                         title: 'cookieValue',
                         tip: 'Cookie Value',
                         type: Type.String,
                         optional: true
                     }) cookieValue:AString):ABoolean | AError
    {
        const sCookieName = cookieName.value.trim();
        if( sCookieName.length == 0 )
            return new AError('#EMPTY_COOKIE_NAME');

        const rootDomain = WindowLocationUtil.getRootDomain();

        if( !cookieValue )
        {
            // treat as "clear cookie"
            CookieUtil.clearCookie(rootDomain, sCookieName);
            return ABoolean.true();
        }

        CookieUtil.setTempCookie(rootDomain, sCookieName, cookieValue.value);

        return ABoolean.true();
    }

    @Function({
        title: 'HAS_COOKIE(cookieName)',
        tip: 'Checks if cookie is present',
        alias: 'HAS_COOKIE',
        example: 'HAS_COOKIE("session_id")',
        icon: '',
        returns:
            {
                title: 'result',
                type: Type.Boolean,
                tip: 'Returns TRUE, if cookie is present and FALSE otherwise'
            }
    })
    @bind
    public hasCookie(@Param({
        title: 'cookieName',
        tip: 'Cookie Name',
        type: Type.String
    }) cookieName:AString):ABoolean
    {
        const cookieValue = CookieUtil.getCookie(cookieName.value);
        return ABoolean.fromBoolean(!!cookieValue);
    }

    @Function({
        title: 'LOGOUT(redirectURL)',
        tip: 'Set cookie value',
        alias: 'LOGOUT',
        example: 'LOGOUT("/login")',
        icon: '',
        returns:
            {
                title: 'result',
                type: Type.String,
                tip: 'Returns TRUE if logout succeeded'
            }
    })
    @bind
    public logout(@Param({
        title: 'redirectURL',
        tip: 'Redirect URL',
        type: Type.String,
        optional: true
    }) redirectURL:AString):ABoolean | AError
    {
        const redirectURLValue:string = redirectURL ? redirectURL.value : '/';
        const hostname:string = WindowLocationUtil.getHostname();
        const bdpAppSessionIdCookie:string = 'bdp_app_session_id';
        const cookieValue:CookieValue = CookieUtil.getCookie(bdpAppSessionIdCookie);

        if( cookieValue )
        {
            CookieUtil.clearCookie(hostname, bdpAppSessionIdCookie);
            window.location.href = redirectURLValue;

            return ABoolean.true();
        }

        return ABoolean.false();
    }

    @Function({
        title: 'CURRENT_USER()',
        tip: 'Returns current user information',
        alias: 'CURRENT_USER',
        example: 'CURRENT_USER()',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Frame with user information',
                tip: 'Frame with user information'
            }
    })
    @bind
    public async currentUser():Promise<IFrame>
    {
        const user = await commonControllers.context.currentUser();
        const fields = [
            new FrameField('id', Type.String),
            new FrameField('name', Type.String)
        ];
        const data = [
            [new AString(user.uuid), new AString(user.fullName)]
        ];
        return Frames.fromFieldsAndData(fields, data);
    }

    @Function({
        title: 'TO_BASE64(data[, isWebSafe])',
        tip: 'Encodes the data into base64 representation.',
        alias: 'TO_BASE64',
        example: 'TO_BASE64("some string to encode, false")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with encoded version of the data parameter',
                tip: 'Returns parameter value as a base64 string'
            }
    })
    @bind
    public toBase64(@Param({
                        title: 'data',
                        tip: 'The data to encode',
                        type: Type.String
                    }) data:AString,
                    @Param({
                        title: 'isWebSafe',
                        tip: 'If true, the web-safe version of base64 encoding will be returned',
                        type: Type.Boolean,
                        optional: true
                    }) isWebSafe:ABoolean):AString
    {
        return isWebSafe && isWebSafe.value ? new AString(EncodingUtil.toWebSafeBase64(EncodingUtil.toBase64(data ? data.value : '')))
            : new AString(EncodingUtil.toBase64(data ? data.value : ''));
    }

    @Function({
        title: 'FROM_BASE64(data[, isWebSafe])',
        tip: 'Decodes the data from base64 representation.',
        alias: 'FROM_BASE64',
        example: 'FROM_BASE64("some string to decode, true")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with decoded version of the data parameter',
                tip: 'Returns parameter value as a base64 string'
            }
    })
    @bind
    public fromBase64(@Param({
                          title: 'base64',
                          tip: 'The base64 data to decode',
                          type: Type.String
                      }) base64:AString,
                      @Param({
                          title: 'isWebSafe',
                          tip: 'If true, the base64 data will be theated as web-safely encoded',
                          type: Type.Boolean,
                          optional: true
                      }) isWebSafe:ABoolean):AString
    {
        return isWebSafe && isWebSafe.value ? new AString(EncodingUtil.fromBase64(EncodingUtil.toBasicBase64(base64 ? base64.value : '')))
            : new AString(EncodingUtil.fromBase64(base64 ? base64.value : ''));
    }

    @Function({
        title: 'HASH_HMAC_SHA1(data, key, isBase64Encoded)',
        tip: 'Creates a hashed for specified data with specified key',
        alias: 'HASH_HMAC_SHA1',
        example: 'HASH_HMAC_SHA1("data, key, isBase64Encoded")',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'String with hashed data',
                tip: 'Returns parameter value as a hmac sha1 hash string'
            }
    })
    @bind
    public hmacSHA1(@Param({
                        title: 'data',
                        tip: 'The data to hash',
                        type: Type.String
                    }) data:AString,
                    @Param({
                        title: 'key',
                        tip: 'Hashing key',
                        type: Type.String
                    }) key:AString,
                    @Param({
                        title: 'isBase64Encoded',
                        tip: 'Should return base64 percent encoded value, hex if false',
                        type: Type.Boolean,
                        optional: true
                    }) isBase64Encoded:ABoolean):AString
    {
        return isBase64Encoded.value ? AString.fromString(EncodingUtil.percentEncode(HmacSHA1(data.value, key.value)
            .toString(enc.Base64))) : AString.fromString(HmacSHA1(data.value, key.value).toString());
    }

    @Function({
        title: 'GET_QUERY_PARAMS()',
        tip: 'Returns all query params as a frame.',
        alias: 'GET_QUERY_PARAMS',
        example: 'GET_QUERY_PARAMS()',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Frame with query params',
                tip: 'Returns query params as a Frame.'
            }
    })
    @bind
    public getQueryParams():IFrame
    {
        const query = window.location.search.substring(1);
        const params = query
            ? (/^[?#]/.test(query) ? query.slice(1) : query)
                .split('&')
                .reduce((params, param) =>
                    {
                        let [key, value] = param.split('=');
                        params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
                        return params;
                    }, {}
                )
            : {};

        return JsonUtil.convertJsonObjectToFrame(params);
    }

    @Function({
        title: 'BROWSER_URL()',
        tip: 'Returns browser url.',
        alias: 'BROWSER_URL',
        example: 'BROWSER_URL()',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'Returns browser url.',
                tip: 'Returns browser url.'
            }
    })
    @bind
    public browserUrl():AString
    {
        return AString.fromString(window.location.href);
    }

    @Function({
        title: 'BROWSER_TOPLEVEL_URL()',
        tip: 'Returns browser top level url.',
        alias: 'BROWSER_TOPLEVEL_URL',
        example: 'BROWSER_TOPLEVEL_URL()',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'Returns browser top level url.',
                tip: 'Returns browser top level url.'
            }
    })
    @bind
    public browserToplevelUrl():AString
    {
        const url = window !== window.top ? document.referrer : window.location.href;

        return AString.fromString(url);
    }

    @Function({
        title: 'OPEN_URL()',
        tip: 'Opens url in browser.',
        alias: 'OPEN_URL',
        example: 'OPEN_URL(url)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                type: Type.String,
                title: 'Returns browser top level url.',
                tip: 'Returns browser top level url.'
            }
    })
    @bind
    public openUrl(@Param({
                       title: 'url',
                       tip: 'Url to open',
                       type: Type.String
                   }) url:AString,
                   @Param({
                       title: 'same-window',
                       tip: 'Open in current window',
                       type: Type.Boolean,
                       optional: true
                   }) isSameWindow:ABoolean
    ):ABoolean
    {
        setTimeout(() =>
        {
            if( isSameWindow && isSameWindow.value )
                window.location.replace(url.value);
            else
                window.open(url.value, '_blank');
        });

        return ABoolean.true();
    }

    private _fetchRequest(method:string, vUrl:AString, body:BodyInit, vHeaders:AFrame, vIncludeHttpDetails:ABoolean, vResponseFormat:AString = null):Promise<IValue | AError>
    {
        const fetchFn = (method:string, url:string, body:BodyInit, headers:any) =>
            fetch(url, {
                method,
                redirect: 'manual', // manual, *follow, error
                // mode:'cors',
                // mode: '' // no-cors, cors, *same-origin
                headers,
                body
            });

        return this._doFetch(
            method,
            vUrl,
            vHeaders,
            vIncludeHttpDetails,
            vResponseFormat,
            body,
            fetchFn
        );
    }

    private _proxyRequest(method:string, vUrl:AString, body:string, vHeaders:AFrame, vIncludeHttpDetails:ABoolean, vResponseFormat:AString = null):Promise<IValue | AError>
    {
        const fetchFn = (method:string, url:string, body:BodyInit, headers:any) =>
            API.proxy.proxyRequest(new ProxyRequest(method, url, body as string, headers));

        return this._doFetch(
            method,
            vUrl,
            vHeaders,
            vIncludeHttpDetails,
            vResponseFormat,
            body,
            fetchFn
        );
    }

    private async _doFetch(method:string, vUrl:AString, vHeaders:AFrame, vIncludeHttpDetails:ABoolean, vResponseFormat:AString, body:BodyInit, fetchFn:FetchFn):Promise<IValue | AError>
    {
        const url = vUrl.value;
        const requiresContentType = method === 'POST' || method === 'PUT' || method === 'PATCH';
        const headers = this._computeHeaders(vHeaders, requiresContentType, body);
        const includeHttpDetails = vIncludeHttpDetails && vIncludeHttpDetails.value || false;
        const responseFormat = vResponseFormat && vResponseFormat.value || 'json';

        if( RESPONSE_FORMATS.indexOf(responseFormat) < 0 )
            return new AError('#INVALID_RESPONSE_FORMAT');

        return fetchFn(method, url, body, headers)
            .then((response) =>
                this._handleResponse(url, response, responseFormat, includeHttpDetails)
            )
            .catch((err) =>
            {
                const errorMessage = err && err.message || err;
                return Frames.forError(`Error: "${errorMessage}", when making http request on URL: ${url}: `);
            });
    }

    private _computeHeaders(headersFrame:AFrame, requiresContentType:boolean, body:BodyInit):any
    {
        const headers = {};

        if( headersFrame != null && !BaseValue.isValueEmpty(headersFrame) &&
            headersFrame.getType() === Type.Frame )
        {
            const records = headersFrame.getRecords();

            for( let record of records )
            {
                const cells = record.getCells();

                if( cells && cells.length && cells.length >= 2 )
                    headers[cells[0].toString()] = cells[1].toString();
            }
        }

        if( requiresContentType )
        {
            const hasContentType = Object.keys(headers).some(headerName =>
                headerName && headerName.toLocaleLowerCase() === 'content-type'
            );

            // we shouldn't set Content-Type for FormData
            // see: https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
            if( !hasContentType && !(body instanceof FormData) )
            {
                headers['Content-Type'] = 'application/json';
            }
        }

        return headers;
    }

    private async _handleResponse(fullURL:string, response:Response, responseFormat:string, includeHttpDetails = false):Promise<IValue | AError>
    {
        if( !response )
            return EvalErrors.Data;

        const responseStatus = response.status;
        const responseHeaders = {};
        response.headers.forEach((value, key) =>
            responseHeaders[key] = value
        );

        if( responseStatus >= 400 && responseStatus <= 499 || responseStatus >= 500 )
        {
            const bodyText = await response.text();
            return JsonUtil.convertJsonObjectToFrame({
                http_status: responseStatus,
                http_headers: responseHeaders,
                http_body: bodyText,
                error: `Server responded with an HTTP status of ${responseStatus} for URL: ${fullURL}`
            });
        }

        let dataValue = null;
        let dataType = null;
        switch( responseFormat )
        {
            case 'json':
            {
                dataValue = JsonUtil.convertJsonObjectToFrame(
                    await response.json()
                );
                dataType = Type.Frame;
                break;
            }
            case 'xml':
            {
                const json = XmlParserUtil.parseXml(
                    await response.text()
                );
                dataValue = JsonUtil.convertJsonObjectToFrame(json);
                dataType = Type.Frame;
                break;
            }
            case 'text':
            {
                dataValue = new AString(
                    await response.text()
                );
                dataType = Type.String;
                break;
            }
            default:
                dataValue = new ABinaryData(new Uint8Array(
                    await response.arrayBuffer()
                ));
                dataType = Type.BinaryData;
                break;
        }

        if( !includeHttpDetails )
            return dataValue;

        const fields = [
            new FrameField('data', dataType),
            new FrameField('http_status', Type.Integer),
            new FrameField('http_headers', Type.Frame)
        ];

        const data = [[
            dataValue,
            new AInteger(responseStatus),
            JsonUtil.convertJsonObjectToFrame(responseHeaders)
        ]];

        return Frames.fromFieldsAndData(fields, data);
    }
}
